import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { FileText, Lock, Mail, Loader2 } from 'lucide-react';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        // Retry logic: try up to 3 times on server errors
        let lastError = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
            try {
                await login(email, password);
                toast.success('Bienvenido al sistema');
                navigate('/dashboard');
                return;
            } catch (error) {
                lastError = error;
                
                // Only retry on server errors (500+), network errors, or timeouts
                const isRetryable = error.response?.status >= 500 || 
                    error.code === 'ECONNABORTED' || 
                    error.code === 'ERR_NETWORK' || 
                    !error.response;
                
                if (error.response?.status === 401) {
                    // Wrong credentials - don't retry
                    toast.error('Credenciales incorrectas. Verifica tu email y contraseña.');
                    setIsLoading(false);
                    return;
                }
                
                if (isRetryable && attempt < 3) {
                    // Wait before retrying
                    await new Promise(r => setTimeout(r, 1500 * attempt));
                    continue;
                }
                
                break;
            }
        }
        
        // All retries failed
        if (lastError) {
            if (lastError.code === 'ECONNABORTED' || lastError.message?.includes('timeout')) {
                toast.error('El servidor está ocupado. Por favor intenta de nuevo.');
            } else if (lastError.code === 'ERR_NETWORK' || !lastError.response) {
                toast.error('Error de conexión. Verifica tu internet.');
            } else if (lastError.response?.status === 503) {
                toast.error('Servidor ocupado. Intenta de nuevo en unos segundos.');
            } else {
                toast.error('Error del servidor. Por favor intenta de nuevo.');
            }
        }
        
        setIsLoading(false);
    };

    return (
        <div className="login-bg min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-md animate-fade-in">
                {/* Logo/Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-500 text-white mb-4 shadow-lg shadow-sky-500/30">
                        <FileText className="w-8 h-8" />
                    </div>
                    <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
                        Contabilidad Clara
                    </h1>
                    <p className="text-slate-500 mt-2">Sistema de gestión de facturas</p>
                </div>

                {/* Login Card */}
                <Card className="border-0 shadow-xl shadow-slate-200/50">
                    <CardHeader className="space-y-1 pb-4">
                        <CardTitle className="text-2xl font-semibold text-center text-slate-800">
                            Iniciar Sesión
                        </CardTitle>
                        <CardDescription className="text-center">
                            Ingresa tus credenciales para acceder
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-700 font-medium">
                                    Correo electrónico
                                </Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="correo@ejemplo.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 h-11 border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                                        required
                                        data-testid="login-email-input"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-700 font-medium">
                                    Contraseña
                                </Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Tu contraseña"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 h-11 border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                                        required
                                        data-testid="login-password-input"
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white font-semibold transition-colors"
                                disabled={isLoading}
                                data-testid="login-submit-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Ingresando...
                                    </>
                                ) : (
                                    'Ingresar'
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Footer */}
                <p className="text-center text-slate-400 text-sm mt-6">
                    Contabilidad Clara - Sistema Seguro
                </p>
            </div>
        </div>
    );
}
